import {Component, OnInit} from '@angular/core';
import {AuthenticationService} from '../authentication.service';
import {MigrationHttpService} from './migration-http.service';
import {MigrationNoticeComponent} from './migration-notice.component';
import {ClaimResultComponent} from './claim-result.component';
import {LegacyClaimStatus} from './migration.models';

/**
 * Decides whether to show the migration notice, so the notice itself stays a dumb presentational component and
 * {@link AppComponent} does not grow another responsibility.
 *
 * Transitional — deleted together with the legacy authorization system at #264.
 */
@Component({
  selector: 'auth-migration-notice-host',
  standalone: true,
  imports: [MigrationNoticeComponent, ClaimResultComponent],
  template: `
    @if (claimed && status) {
      <auth-claim-result [status]="status" (closed)="closeResult()"/>
    } @else if (show && status) {
      <auth-migration-notice [status]="status" [asEmployee]="asEmployee" (dismissed)="dismiss()"
                             (claimed)="nativeClaimed()"/>
    }
  `
})
export class MigrationNoticeHostComponent implements OnInit {
  /**
   * Which SESSION the notice was dismissed for — the session's own JWT, not a boolean.
   *
   * Since #854 this is only a fast path. The source of truth is the server: every way of closing the notice
   * calls `POST /auth/migration/notice-seen`, which stamps the session's token row, and the status answers
   * `shouldPrompt: false` for that token from then on. sessionStorage alone could not carry it, because it is
   * per tab (a new tab showed the notice again), and localStorage is consent-trapped in this app.
   *
   *
   * A boolean in sessionStorage looked right and was not: sessionStorage is scoped to the browser TAB, so
   * signing out and back in without closing it kept the dismissal, and the notice never returned. Storing the
   * token means any new login writes a different value, so "dismissed" is true only for the exact session that
   * dismissed it — which is the cadence intended all along.
   *
   * NB: sessionStorage, never localStorage. CookieService replaces the global localStorage.setItem with a
   * function that THROWS unless the write goes through its consent-aware wrapper; sessionStorage is untrapped.
   */
  private static readonly DismissedKey = 'cheqme.migration-notice.dismissed-for';

  protected show = false;
  protected claimed = false;
  protected status: LegacyClaimStatus | null = null;
  protected asEmployee = false;

  constructor(private http: MigrationHttpService, private auth: AuthenticationService) {
  }

  ngOnInit(): void {
    const session = this.auth.userValue;
    if (!session) return;

    // The claim callback returns here with ?claimed=success. Read it before the dismissal check: somebody who
    // dismissed the notice earlier in this session and then claimed from the profile page must still be told it
    // worked, and still be offered the password switch-off.
    this.claimed = new URLSearchParams(location.search).get('claimed') === 'success';

    // A provider claim that came back successful ends the notice for this session (#854), even if the
    // confirmation is never closed: a reload with the parameter still in the URL shows the confirmation again,
    // never the notice.
    if (this.claimed) this.rememberDismissal();

    // Compared against THIS session's token, so a re-login in the same tab shows the notice again.
    if (!this.claimed
        && sessionStorage.getItem(MigrationNoticeHostComponent.DismissedKey) === session.jwt) return;

    this.http.status().subscribe({
      // The server decides (#854): once per session for a password session, never for an impersonation.
      // `isLegacySession` alone was the old rule, and it is true for every password session for as long as it
      // lives, so the full-screen notice reopened on every reload and in every new tab.
      next: status => {
        this.status = status;
        this.asEmployee = this.auth.isPanelUser();
        this.show = status.shouldPrompt === true;
      },
      // A failed status check must never block the app. Someone who cannot reach the endpoint still has work to
      // do, and the notice will be offered again next time.
      error: () => {
        this.show = false;
        this.claimed = false;
      }
    });
  }

  protected dismiss(): void {
    this.show = false;
    this.rememberDismissal();
  }

  /**
   * The native claim (#279) finished, on this page, with no navigation.
   *
   * It reaches the SAME confirmation panel the three provider claims reach, which is the point: what happened is
   * identical — a login was handed to an account — and the offer that follows (switching password login off) is
   * identical too. Only the route here is shorter.
   *
   * `canDisablePasswordLogin` is refreshed from the result rather than left at whatever the status said on load.
   * It was false for anybody claiming for the first time — that is precisely who this notice is shown to — and
   * the panel gates its offer on it, so re-using the stale value would hide the switch-off from every first
   * migration. There is no reload here to fetch a new status, which is exactly why the endpoint answers with the
   * new value.
   */
  protected nativeClaimed(): void {
    if (this.status) this.status = {...this.status, canDisablePasswordLogin: true};

    this.show = false;
    this.claimed = true;
    // #854: the login is claimed, so this session is done with the notice, exactly as if it had been dismissed.
    this.rememberDismissal();
  }

  /**
   * Closes the post-claim panel and strips `claimed` from the URL, so a reload or a shared link does not
   * re-announce a migration that already happened.
   */
  protected closeResult(): void {
    this.claimed = false;
    this.show = false;
    // #854: the confirmation is the end of the migration for this session, for a provider claim
    // (`?claimed=success`) as much as for the native one. Without this, only the "stay on the password" button
    // ever wrote the dismissal.
    this.rememberDismissal();

    const url = new URL(location.href);
    url.searchParams.delete('claimed');
    history.replaceState({}, '', url.toString());
  }

  /**
   * Records that THIS session is done with the notice: on the server (the source of truth, #854) and in
   * sessionStorage (a fast path that saves the status round trip in this tab, see {@link DismissedKey}).
   *
   * Fire and forget. A failed call must not block anybody; the worst case is that the notice shows once more on
   * the next bootstrap of this session.
   */
  private rememberDismissal(): void {
    const jwt = this.auth.userValue?.jwt;
    if (jwt) sessionStorage.setItem(MigrationNoticeHostComponent.DismissedKey, jwt);

    this.http.noticeSeen().subscribe({error: () => undefined});
  }
}
